import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, Play, ShieldStar, Eye } from '@phosphor-icons/react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/api';
import { TILE_GUIDE } from '../data/tileGuide';

const OBSIDIAN = '#05050A';
const GOLD = '#D4AF37';
const GOLD_40 = 'rgba(212,175,55,0.4)';
const SAPPHIRE_50 = 'rgba(15,82,186,0.5)';

/* ─── HERO: LION & EAGLE ─── */
const LION_EAGLE_IMG = 'https://static.prod-images.emergentagent.com/jobs/c1791a41-c4bf-4dc4-bd4c-f4956e74cfc4/images/287ffc1b161e1eac23f5e45afadf65d7196704a73cd25bab01adcfc75f923299.png';

/* ─── MASSIVE 3D SCULPTURES ─── */
const ICON_LEOPARD  = '/icons/leopard_v2.png';
const ICON_GAZELLE  = '/icons/gazelle.png';
const ICON_FALCON   = '/icons/falcon_v2.png';
const ICON_PHOENIX  = '/icons/phoenix_v2.png';
const ICON_GORILLA  = '/icons/gorilla_v2.png';
const ICON_STALLION = '/icons/stallion_v2.png';
const ICON_CROWN    = '/icons/lion_legacy.png';
const ICON_CRANE    = '/icons/crystal_crane.png';

/* ─── BG TEXTURES ─── */
const TEXTURE_IMG = 'https://static.prod-images.emergentagent.com/jobs/c1791a41-c4bf-4dc4-bd4c-f4956e74cfc4/images/f5340c8a2b6a9ccd68c9aa0ab83b7e4f88c2d62a247ff37c605fdb04d64f0daa.png';
const LEGACY_BG = 'https://static.prod-images.emergentagent.com/jobs/c1791a41-c4bf-4dc4-bd4c-f4956e74cfc4/images/f0bacb8e0a50f150d1226bf71f277a00e441b72d810c66805e734bf3c1b4055c.png';

/* ─── CARD DATA — The Society Crests (Bliss Society Top 10 shields) ─── */
const CREST_CARDS = [
  { id: 'fox',     name: 'Pronoia', animal: 'The Golden Fox',     meaning: 'The universe conspires for you.', icon: '/society/fox.jpg', cover: true,     subtitle: 'Golden Crystal Fox · Top 10 Pronoia' },
  { id: 'panther', name: 'Aura',    animal: 'The Black Panther',  meaning: 'Power fused, pillar by pillar.',  icon: '/society/panther.jpg', cover: true, subtitle: 'Black Crystal Panther · Top 10 Aura' },
  { id: 'peacock', name: 'Vibes',   animal: 'The White Peacock',  meaning: 'Your life, displayed in light.',  icon: '/society/peacock.jpg', cover: true, subtitle: 'White Crystal Peacock · Top 10 Vibes' },
];

/* ─── CARD DATA — The Imperium Eight ─── */
const VIBE_CARDS = [
  { id: 'fashion',  name: 'Fashion',  animal: 'The Leopard',   meaning: 'Elegance in Every Detail.',    icon: ICON_LEOPARD,  subtitle: 'Polished Gold with High-Gloss Reflection' },
  { id: 'taste',    name: 'Taste',    animal: 'The Gazelle',   meaning: 'The Sovereign Palate.',        icon: ICON_GAZELLE,  subtitle: 'Gold Gazelle with Sapphire Chalice' },
  { id: 'explore',  name: 'Explore',  animal: 'The Falcon',    meaning: 'Beyond the Horizon.',          icon: ICON_FALCON,   subtitle: 'Silver Chrome with Sapphire Eyes' },
  { id: 'souls',    name: 'Soul',     animal: 'The Phoenix',   meaning: 'The Quantum Heart Reborn.',    icon: ICON_PHOENIX,  subtitle: 'Ruby-Red Crystal with White-Gold Sparks' },
  { id: 'sporty',   name: 'Sport',    animal: 'The Gorilla',   meaning: 'Dominance in Motion.',         icon: ICON_GORILLA,  subtitle: 'Obsidian Mask with Emerald Eyes' },
  { id: 'success',  name: 'Success',  animal: 'The Stallion',  meaning: 'The Throne Awaits.',           icon: ICON_STALLION, subtitle: 'Polished Gold on Diamond Block' },
  { id: 'legacy',   name: 'Legacy',   animal: 'The Lion',      meaning: 'Immortality Through Memory.',  icon: ICON_CROWN,    subtitle: 'Gold Obsidian Lion with Royal Mane' },
  { id: 'art',      name: 'Art',      animal: 'The Crane',     meaning: 'Grace Carved in Crystal.',     icon: ICON_CRANE,    subtitle: 'Crystal Crane, Wings of Living Light' },
];

/* ─── ROYAL CODEX — the 24 tiles (shared guide) ─── */
const CODEX_RITUALS = TILE_GUIDE.map((t) => ({ id: t.id, label: t.name, accent: t.accent, tag: t.tag }));

/* ─── MASSIVE VIBE CARD (sculpture fills 80%) ─── */
const VibeCard = ({ card, index }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <motion.div
      className="perspective-1000 cursor-pointer"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-30px' }}
      transition={{ delay: index * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => setFlipped(!flipped)}
      data-testid={`vibe-card-${card.id}`}
    >
      <motion.div
        className="relative w-full aspect-[3/4]"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* FRONT — Massive Sculpture */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden border border-white/[0.06] group"
          style={{ backfaceVisibility: 'hidden', background: 'linear-gradient(180deg, #0a0a0f 0%, #05050A 100%)' }}
        >
          {/* Sculpture image — fills 80% */}
          <div className={card.cover ? 'absolute inset-x-2 top-2 rounded-xl overflow-hidden' : 'absolute inset-0 flex items-center justify-center p-3'}
            style={card.cover ? { bottom: '34%' } : undefined}>
            <img
              src={card.icon}
              alt={card.animal}
              className={`${card.cover ? 'w-full h-full object-cover' : 'w-[85%] h-[85%] object-contain'} drop-shadow-[0_0_20px_rgba(212,175,55,0.15)] group-hover:drop-shadow-[0_0_35px_rgba(212,175,55,0.3)] transition-all duration-700 group-hover:scale-105`}
              loading="lazy"
            />
          </div>
          {/* Gradient overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20" />
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-[#D4AF37]/20" />
          <div className="absolute top-0 right-0 w-5 h-5 border-t border-r border-[#D4AF37]/20" />
          <div className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-[#D4AF37]/20" />
          <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-[#D4AF37]/20" />
          {/* Text overlay at bottom */}
          <div className={`absolute bottom-0 inset-x-0 z-10 ${card.cover ? 'px-2.5 pb-3' : 'p-4'}`}>
            <h3 className={`font-['Cormorant_Garamond'] font-light text-white tracking-wide ${card.cover ? 'text-lg sm:text-2xl leading-tight' : 'text-xl sm:text-2xl'}`}>{card.name}</h3>
            <span className={`font-['Outfit'] uppercase text-[#D4AF37]/60 block mt-0.5 ${card.cover ? 'text-[7px] sm:text-[9px] tracking-[0.12em] leading-snug' : 'text-[8px] sm:text-[9px] tracking-[0.2em]'}`}>{card.animal}</span>
          </div>
          <div className="absolute top-3 right-3 font-['Outfit'] text-[7px] uppercase tracking-[0.15em] text-white/15 z-10">Tap</div>
        </div>

        {/* BACK — Core Meaning */}
        <div
          className={`absolute inset-0 rounded-2xl border border-[#D4AF37]/30 flex flex-col items-center justify-center overflow-hidden ${card.cover ? 'gap-2 p-2.5' : 'gap-4 p-5'}`}
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', background: 'linear-gradient(180deg, #0c0c14 0%, #05050A 100%)' }}
        >
          <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <p className={`font-['Cormorant_Garamond'] font-light text-[#D4AF37] text-center italic leading-snug ${card.cover ? 'text-[14px] sm:text-xl' : 'text-xl sm:text-2xl'}`}>
            "{card.meaning}"
          </p>
          <p className={`font-['Outfit'] text-white/25 text-center uppercase ${card.cover ? 'text-[7.5px] sm:text-[9px] tracking-[0.06em] leading-snug' : 'text-[8px] sm:text-[9px] tracking-[0.15em]'}`}>{card.subtitle}</p>
          <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <span className={`font-['Outfit'] uppercase text-white/20 mt-1 ${card.cover ? 'text-[7px] tracking-[0.15em]' : 'text-[9px] tracking-[0.25em]'}`}>Core Truth</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ─── LEGACY WALL ─── */
const LegacyWall = ({ memories }) => {
  if (!memories || memories.length === 0) return null;
  const doubled = [...memories, ...memories];

  return (
    <div className="relative overflow-hidden py-2" data-testid="legacy-wall">
      <motion.div
        className="flex gap-4"
        animate={{ x: [0, -(memories.length * 220)] }}
        transition={{ duration: memories.length * 4, repeat: Infinity, ease: 'linear' }}
      >
        {doubled.map((mem, i) => (
          <div key={`${mem.id}-${i}`} className="flex-shrink-0 w-[200px] h-[260px] rounded-xl overflow-hidden border border-white/[0.06] relative group">
            {mem.media_type === 'video' ? (
              <div className="w-full h-full bg-black/60 flex items-center justify-center">
                <Play size={32} weight="fill" className="text-white/40" />
              </div>
            ) : (
              <img src={mem.image_url} alt={mem.note || ''} className="w-full h-full object-cover" loading="lazy" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute bottom-0 inset-x-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]">{mem.category}</span>
              {mem.note && <p className="font-['Outfit'] text-xs text-white/70 line-clamp-2 mt-1">{mem.note}</p>}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

/* ─── PULSING GOLD GLOW ─── */
const PulseStyle = () => (
  <style>{`
    @keyframes goldPulse {
      0%, 100% { box-shadow: 0 0 15px rgba(212,175,55,0.3), inset 0 0 15px rgba(212,175,55,0.15); border-color: rgba(212,175,55,0.3); }
      50% { box-shadow: 0 0 30px rgba(212,175,55,0.6), inset 0 0 25px rgba(212,175,55,0.25); border-color: rgba(212,175,55,0.6); }
    }
    .gold-pulse-border { animation: goldPulse 2.5s ease-in-out infinite; }
  `}</style>
);

/* ─── MAIN LANDING PAGE ─── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [memories, setMemories] = useState([]);
  const heroRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/legacy-wall?limit=12`)
      .then(({ data }) => setMemories(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let lenis;
    const init = async () => {
      try {
        const { default: Lenis } = await import('lenis');
        lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
        const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
        requestAnimationFrame(raf);
      } catch {}
    };
    init();
    return () => { if (lenis) lenis.destroy(); };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: OBSIDIAN }} data-testid="imperium-landing">
      <PulseStyle />

      {/* ══════ HERO — LION & EAGLE (Primary Brand) ══════ */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 70%, ${SAPPHIRE_50} 0%, transparent 50%)` }} />
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 30%, ${GOLD_40} 0%, transparent 40%)` }} />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#D4AF37]/60" />
            <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/70">Imperium Core</span>
            <div className="w-8 h-[1px] bg-[#D4AF37]/60" />
          </motion.div>

          {/* LION & EAGLE — Large 3D Chrome Statue */}
          <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="mb-4 relative">
            <div className="w-48 h-48 sm:w-64 sm:h-64 lg:w-80 lg:h-80 rounded-full overflow-hidden border-2 border-[#D4AF37]/30 relative" style={{ boxShadow: `0 0 60px ${GOLD_40}, 0 0 120px rgba(15,82,186,0.2)` }}>
              <img src={LION_EAGLE_IMG} alt="The Core — Lion & Eagle" className="w-full h-full object-cover scale-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050A]/40 to-transparent" />
            </div>
            {/* Bliss 'B' icon badge */}
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border border-[#D4AF37]/50 bg-[#05050A]" style={{ boxShadow: `0 0 20px ${GOLD_40}` }}>
              <img src="/icons/bliss_icon.png" alt="Bliss" className="w-full h-full object-cover" />
            </div>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }} className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-8xl font-light text-white tracking-tight leading-[0.95]">
            <span className="text-[#D4AF37]">#ARSH</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/25 mt-2">The Imperium Core</motion.p>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }} className="font-['Outfit'] text-sm sm:text-base text-white/40 mt-4 sm:mt-5 max-w-md leading-relaxed">
            Authority and Vision. Fashion. Taste. Exploration. Soul. Power.
            <br className="hidden sm:block" />
            <span className="text-[#D4AF37]/60">Every vibe is a throne.</span>
          </motion.p>

          {/* CTA → Register */}
          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }}
            whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${GOLD_40}` }} whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/register')}
            className="mt-8 sm:mt-10 px-8 sm:px-12 py-3.5 sm:py-4 border border-[#D4AF37] text-[#D4AF37] font-['Outfit'] text-sm uppercase tracking-[0.2em] rounded-none hover:bg-[#D4AF37] hover:text-black transition-all duration-500"
            style={{ boxShadow: `0 0 15px ${GOLD_40}` }}
            data-testid="cta-claim-rank"
          >
            Claim Your Rank
          </motion.button>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }} className="absolute bottom-8 flex flex-col items-center gap-2">
          <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em] text-white/20">Discover</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}><ArrowDown size={18} weight="thin" className="text-white/20" /></motion.div>
        </motion.div>
      </section>

      {/* ══════ IMPERIUM EIGHT GRID ══════ */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6" data-testid="imperium-grid-section">
        <div className="absolute inset-0 opacity-10"><img src={TEXTURE_IMG} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-12 sm:mb-16">
            <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/50">The Imperium Eight</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-3 tracking-tight">Choose Your <span className="text-[#D4AF37]">Domain</span></h2>
            <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent mx-auto mt-6" />
            <p className="font-['Cormorant_Garamond'] italic text-base sm:text-lg text-white/40 mt-5 max-w-xl mx-auto leading-snug">
              Eight thrones. Eight beasts. Eight chambers of your sovereign self.
            </p>
          </motion.div>

          {/* 8 cards — 2 cols mobile, 4 cols desktop (clean 2×4 grid) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5">
            {VIBE_CARDS.map((card, i) => (
              <VibeCard key={card.id} card={card} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════ THE SOCIETY CRESTS ══════ */}
      <section className="relative py-14 sm:py-20 px-4 sm:px-6" data-testid="society-crests-section">
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10 sm:mb-14">
            <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/50">The Bliss Society</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-3 tracking-tight">Three <span className="text-[#D4AF37]">Crests</span></h2>
            <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent mx-auto mt-6" />
            <p className="font-['Cormorant_Garamond'] italic text-base sm:text-lg text-white/40 mt-5 max-w-xl mx-auto leading-snug">
              Rise into the Top 10 of the Fox, the Panther or the Peacock — and wear the crest as your sigil.
            </p>
          </motion.div>
          <div className="grid grid-cols-3 gap-3 sm:gap-5">
            {CREST_CARDS.map((card, i) => (
              <VibeCard key={card.id} card={card} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════ THE SOVEREIGN DOCTRINE — Manifesto ══════ */}
      <section className="relative py-20 sm:py-28 px-6" data-testid="doctrine-section">
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 50%, rgba(15,82,186,0.18) 0%, transparent 60%)` }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/60">The Sovereign Doctrine</span>
            <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent mx-auto mt-4 mb-8" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.2 }}
            className="font-['Cormorant_Garamond'] italic text-2xl sm:text-3xl lg:text-4xl font-light text-white/85 leading-snug"
          >
            <span className="text-[#D4AF37] text-5xl sm:text-6xl font-normal align-middle mr-1">“</span>
            You were not born to be average. You were born to be <span className="text-[#D4AF37]">indelible</span>. The Imperium is not an app — it is the codex of your reign.
            <span className="text-[#D4AF37] text-5xl sm:text-6xl font-normal align-middle ml-1">”</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.4 }}
            className="mt-8 flex items-center justify-center gap-3"
          >
            <div className="w-8 h-[1px] bg-[#D4AF37]/40" />
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.3em] text-white/30">#ARSH · Imperium Core</span>
            <div className="w-8 h-[1px] bg-[#D4AF37]/40" />
          </motion.div>
        </div>
      </section>

      {/* ══════ ROYAL CODEX — The 24 Rituals ══════ */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6" data-testid="royal-codex-section">
        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10 sm:mb-14">
            <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]/50">The Royal Codex</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-3 tracking-tight">Twenty-Four <span className="text-[#D4AF37]">Rituals</span> Within</h2>
            <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent mx-auto mt-6" />
            <p className="font-['Outfit'] text-sm text-white/40 mt-5 max-w-md mx-auto leading-relaxed">
              Each ritual a chamber. Each chamber a vow. Step inside the codex of your reign.
            </p>
          </motion.div>

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
            {CODEX_RITUALS.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.6 }}
                whileHover={{ y: -3 }}
                onClick={() => navigate('/features')}
                className="cursor-pointer rounded-xl border border-white/[0.07] py-4 sm:py-5 flex flex-col items-center gap-2 transition-colors hover:border-[#D4AF37]/40"
                style={{ background: 'rgba(10,10,16,0.7)' }}
                data-testid={`codex-chip-${r.id}`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: r.accent, boxShadow: `0 0 14px ${r.accent}` }}
                />
                <span className="font-['Cormorant_Garamond'] text-base sm:text-lg text-white/85 tracking-wide">{r.label}</span>
                <span className="font-['Cormorant_Garamond'] italic text-[11px] text-white/45 px-1 text-center leading-tight">{r.tag}</span>
                <span className="font-['Outfit'] text-[8px] uppercase tracking-[0.2em] text-white/25">{String(i + 1).padStart(2, '0')}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 sm:mt-12 flex justify-center">
            <motion.button
              whileHover={{ scale: 1.04, boxShadow: `0 0 24px ${GOLD_40}` }} whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/features')}
              className="px-7 sm:px-9 py-3 border border-[#D4AF37]/60 text-[#D4AF37] font-['Outfit'] text-xs uppercase tracking-[0.25em] hover:bg-[#D4AF37] hover:text-black transition-all duration-500"
              data-testid="cta-explore-codex"
            >
              Explore The Codex
            </motion.button>
          </div>
        </div>
      </section>

      {/* ══════ LEGACY WALL ══════ */}
      {memories.length > 0 && (
        <section className="relative py-16 sm:py-24 overflow-hidden" data-testid="legacy-wall-section">
          <div className="absolute inset-0 opacity-20"><img src={LEGACY_BG} alt="" className="w-full h-full object-cover" /></div>
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${OBSIDIAN} 0%, transparent 20%, transparent 80%, ${OBSIDIAN} 100%)` }} />
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10">
              <span className="font-['Outfit'] text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#0F52BA]/70">Live from the Empire</span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light text-white mt-3 tracking-tight">The <span className="text-[#D4AF37]">Legacy</span> Wall</h2>
              <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#0F52BA]/40 to-transparent mx-auto mt-6" />
            </motion.div>
            <div className="rounded-2xl border border-[#0F52BA]/20 p-4 sm:p-6" style={{ boxShadow: `0 0 30px ${SAPPHIRE_50}`, background: 'rgba(5,5,10,0.6)' }}>
              <LegacyWall memories={memories} />
            </div>
          </div>
        </section>
      )}

      {/* ══════ FINAL CTA ══════ */}
      <section className="relative py-20 sm:py-32 px-4" data-testid="final-cta-section">
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 50%, ${GOLD_40} 0%, transparent 50%)` }} />
        <div className="relative z-10 flex flex-col items-center text-center max-w-2xl mx-auto">
          <ShieldStar size={40} weight="thin" className="text-[#D4AF37] mb-4" style={{ filter: `drop-shadow(0 0 15px ${GOLD_40})` }} />
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight">Your <span className="text-[#D4AF37]">Empire</span> Awaits</h2>
          <p className="font-['Outfit'] text-sm text-white/30 mt-4 max-w-sm leading-relaxed">Join the ranks. Document every triumph. Build your legacy.</p>
          <motion.button whileHover={{ scale: 1.05, boxShadow: `0 0 40px ${GOLD_40}` }} whileTap={{ scale: 0.98 }} onClick={() => navigate('/register')} className="mt-8 px-10 sm:px-14 py-4 bg-[#D4AF37] text-black font-['Outfit'] text-sm uppercase tracking-[0.2em] rounded-none hover:bg-[#E5C44D] transition-all duration-500" style={{ boxShadow: `0 0 20px ${GOLD_40}` }} data-testid="cta-final">
            Claim Your Rank
          </motion.button>
        </div>
      </section>

      {/* ══════ ADMOB BANNER (pulsing gold glow) ══════ */}
      <section className="px-4 sm:px-6 pb-8" data-testid="admob-banner-slot">
        <div className="max-w-5xl mx-auto">
          <div className="gold-pulse-border rounded-xl border border-[#D4AF37]/30 bg-black/40 backdrop-blur-xl flex items-center justify-center min-h-[90px] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-[#D4AF37]/50" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[#D4AF37]/50" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-[#D4AF37]/50" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-[#D4AF37]/50" />
            <div className="text-center py-4 px-6" id="admob-banner-container">
              <span className="font-['Outfit'] text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/40">Executive Partner</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer className="border-t border-white/[0.05] py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="font-['Cormorant_Garamond'] text-lg text-white/60 font-light"><span className="text-[#D4AF37]">#ARSH</span></span>
            <span className="text-white/10 mx-2">|</span>
            <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.2em] text-white/20">Imperium Core</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/login')} className="font-['Outfit'] text-xs text-white/30 hover:text-[#D4AF37] transition-colors uppercase tracking-[0.15em]" data-testid="footer-login">Sign In</button>
            <button onClick={() => navigate('/register')} className="font-['Outfit'] text-xs text-white/30 hover:text-[#D4AF37] transition-colors uppercase tracking-[0.15em]" data-testid="footer-register">Join</button>
            <button onClick={() => navigate('/privacy')} className="font-['Outfit'] text-xs text-white/30 hover:text-[#D4AF37] transition-colors uppercase tracking-[0.15em]" data-testid="footer-privacy">Privacy Policy</button>
          </div>
        </div>
      </footer>
      {/* Spacer so the fixed badge only shows at absolute bottom scroll */}
      <div className="h-16" style={{ background: OBSIDIAN }} />
    </div>
  );
}
